import React from 'react';
import SimpleModal from '../../../components/common/SimpleModal';

interface BannerOnboardingGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 「設定画面に戻る」ボタン。モーダルを閉じてページ操作に戻すだけ（onClose と同じで良い）。 */
  onStart: () => void;
}

export interface BannerOnboardingGuideVisibilityInput {
  /** URL クエリ `onboarding=1` が付いているか。 */
  onboardingParam: boolean;
  /** このセッションで一度閉じられたか（再表示させない）。 */
  dismissed: boolean;
  /** localStorage の既読フラグ（tasky_banner_guide_seen）が立っているか。 */
  seen: boolean;
  /**
   * onboardingV2Service.getProgress() の generate_done。
   * 取得前・取得失敗は null（この場合 (b) 条件では出さない）。
   */
  generateDone: boolean | null;
}

/**
 * /ads/banner のオンボーディング案内モーダルの表示可否。
 * (a) /home のチェックリストから `?onboarding=1` で来た時は、このセッションで未 dismiss なら
 *     seen / progress に関係なく出す（明示的な導線）。
 * (b) それ以外は、初回訪問（未 seen）かつ まだ自分でバナーを作っていない
 *     （generate_done === false）時だけ出す。progress 取得失敗/取得前（null）では出さない。
 */
export const shouldShowBannerOnboardingGuide = ({
  onboardingParam,
  dismissed,
  seen,
  generateDone,
}: BannerOnboardingGuideVisibilityInput): boolean => {
  if (dismissed) return false;
  if (onboardingParam) return true;

  return !seen && generateDone === false;
};

const STEPS = [
  { number: 1, title: '商品または広告の内容を選ぶ', description: '「商品を選択」で登録済みの商品を選びます。「フリーテキスト」なら希望を直接入力できます。' },
  {
    number: 2,
    title: 'デザイン・サイズ・枚数を決める',
    description: '「スタイルを選ぶ」で見本から選べます。選ばなければ商品に合わせて自動で選びます。用途に合うサイズと枚数も確認してください。',
  },
  { number: 3, title: '生成した画像を開いて保存する', description: '「生成」を押し、完成した画像を開きます。「画像を編集」で修正、「ダウンロード」で端末に保存できます。' },
];

const BannerOnboardingGuideModal: React.FC<BannerOnboardingGuideModalProps> = ({
  isOpen,
  onClose,
  onStart,
}) => (
  <SimpleModal isOpen={isOpen} onClose={onClose} title="バナー制作の使い方" size="sm">
    <div className="flex flex-col gap-5">
      <ol className="m-0 flex list-none flex-col gap-4 p-0">
        {STEPS.map((step) => (
          <li key={step.number} className="flex items-start gap-3">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
              {step.number}
            </span>
            <span className="min-w-0 flex-1 pt-0.5">
              <span className="block text-sm font-semibold text-dark">{step.title}</span>
              <span className="block text-sm leading-relaxed text-gray-500">{step.description}</span>
            </span>
          </li>
        ))}
      </ol>
      <div className="flex justify-end gap-2.5">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-border bg-white px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-warm-surface"
        >
          閉じる
        </button>
        <button
          type="button"
          onClick={onStart}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-hover"
        >
          設定画面に戻る
        </button>
      </div>
    </div>
  </SimpleModal>
);

export default BannerOnboardingGuideModal;
